import React, { useState, useMemo } from "react";
import { Plus, Minus, Zap, History, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useList, useDeleteOne } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState, ProgressBar, fmt, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const SHIFTS = ["Shift A (6am-2pm)", "Shift B (2pm-10pm)", "Shift C (10pm-6am)"];

function Counter({ label, value, onChange, tone, testid }) {
  const set = (v) => onChange(Math.max(0, v));
  const color = tone === "danger" ? "text-destructive" : tone === "warning" ? "text-warning" : "text-success";
  return (
    <div className="rounded-xl border border-border p-4 bg-secondary/30">
      <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">{label}</div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={() => set(value - 1)} data-testid={`${testid}-minus`}><Minus className="w-4 h-4" /></Button>
        <Input type="number" value={value} onChange={(e) => set(Number(e.target.value) || 0)} className={`h-11 text-center text-2xl font-num font-bold ${color}`} data-testid={testid} />
        <Button type="button" variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={() => set(value + 1)} data-testid={`${testid}-plus`}><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="flex gap-1.5 mt-2">
        {[5, 10, 50].map((n) => (
          <Button key={n} type="button" variant="ghost" size="sm" className="flex-1 h-8 text-xs" onClick={() => set(value + n)}>+{n}</Button>
        ))}
      </div>
    </div>
  );
}

export default function Production() {
  const { canWrite } = useAuth();
  const qc = useQueryClient();
  const delEntry = useDeleteOne((id) => `/production/${id}`, ["production", "orders", "dashboard"]);
  const { data: orders = [], isLoading } = useList("orders", "/orders");
  const { data: departments = [] } = useList("departments", "/departments");
  const { data: stages = [] } = useList("stages", "/stages");
  const { data: machines = [] } = useList("machines", "/machines");

  const [orderId, setOrderId] = useState("");
  const [dept, setDept] = useState("");
  const [stage, setStage] = useState("");
  const [machine, setMachine] = useState("");
  const [shift, setShift] = useState("");
  const [operator, setOperator] = useState("");
  const [good, setGood] = useState(0);
  const [rej, setRej] = useState(0);
  const [rework, setRework] = useState(0);
  const [notes, setNotes] = useState("");
  const [override, setOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const order = useMemo(() => orders.find((o) => o.id === orderId), [orders, orderId]);
  const { data: entries = [] } = useList("production", "/production", orderId ? { order_id: orderId } : undefined);

  const canOverride = canWrite("production");
  const wouldExceed = order && (order.produced_quantity + good) > order.order_quantity;

  const submit = async () => {
    if (!canWrite("production")) return toast.error("You don't have permission to enter production");
    if (!orderId) return toast.error("Select an order");
    if (!dept) return toast.error("Select a department");
    if (good + rej + rework === 0) return toast.error("Enter at least one quantity");
    setSubmitting(true);
    try {
      const machineObj = machines.find((m) => m.id === machine);
      await api.post("/production", {
        order_ref: orderId, department: dept, stage: stage || dept, machine_id: machine || null,
        machine_name: machineObj?.name || null, shift, operator, good_quantity: good,
        rejected_quantity: rej, rework_quantity: rework, notes, override,
      });
      toast.success(`Recorded ${good} pcs on ${order.order_id}`);
      setGood(0); setRej(0); setRework(0); setNotes(""); setOverride(false);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Entry failed");
    } finally { setSubmitting(false); }
  };

  if (isLoading) return <Loading />;

  return (
    <div>
      <PageHeader title="Live Production Entry" subtitle="Fast shop-floor entry — totals update instantly" testid="production-header" />

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Entry form */}
        <Card className="lg:col-span-3 p-5" data-testid="production-entry-card">
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <div className="sm:col-span-2">
              <Label>Order</Label>
              <Select value={orderId} onValueChange={setOrderId}>
                <SelectTrigger data-testid="prod-order-select"><SelectValue placeholder="Select an order" /></SelectTrigger>
                <SelectContent>
                  {orders.filter((o) => !["Dispatched"].includes(o.status)).map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.order_id} — {o.product_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department</Label>
              <Select value={dept} onValueChange={setDept}>
                <SelectTrigger data-testid="prod-dept-select"><SelectValue placeholder="Department" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stage</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger data-testid="prod-stage-select"><SelectValue placeholder="Stage" /></SelectTrigger>
                <SelectContent>{stages.filter((s) => s.active).map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Machine</Label>
              <Select value={machine} onValueChange={setMachine}>
                <SelectTrigger data-testid="prod-machine-select"><SelectValue placeholder="Machine" /></SelectTrigger>
                <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Shift</Label>
              <Select value={shift} onValueChange={setShift}>
                <SelectTrigger data-testid="prod-shift-select"><SelectValue placeholder="Shift" /></SelectTrigger>
                <SelectContent>{SHIFTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Operator / Worker</Label>
              <Input value={operator} onChange={(e) => setOperator(e.target.value)} placeholder="Name" data-testid="prod-operator-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Counter label="Good Qty" value={good} onChange={setGood} tone="success" testid="prod-good-input" />
            <Counter label="Rejected" value={rej} onChange={setRej} tone="danger" testid="prod-rejected-input" />
            <Counter label="Rework" value={rework} onChange={setRework} tone="warning" testid="prod-rework-input" />
          </div>

          {wouldExceed && (
            <div className="mt-3 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm">
              <p className="font-semibold text-destructive">⚠ Entry exceeds order quantity ({fmt(order.order_quantity)}).</p>
              {canOverride ? (
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <Checkbox checked={override} onCheckedChange={setOverride} data-testid="prod-override-check" />
                  <span>Authorize override (Admin / Manager)</span>
                </label>
              ) : <p className="text-muted-foreground mt-1">Requires Admin / Production Manager override.</p>}
            </div>
          )}

          <div className="mt-3"><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></div>

          <Button onClick={submit} disabled={submitting} className="w-full h-14 mt-4 text-lg font-bold gap-2" data-testid="production-submit-btn">
            <Zap className="w-5 h-5" /> {submitting ? "Recording…" : "Record Production"}
          </Button>
        </Card>

        {/* Live order summary */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5" data-testid="production-order-summary">
            {order ? (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-num font-bold text-lg text-primary">{order.order_id}</div>
                    <div className="text-sm text-muted-foreground">{order.product_name}</div>
                  </div>
                  <StatusBadge status={order.status} />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <Stat label="Target" value={fmt(order.order_quantity)} />
                  <Stat label="Completed" value={fmt(order.produced_quantity)} tone="success" />
                  <Stat label="Rejected" value={fmt(order.rejected_quantity)} tone="danger" />
                  <Stat label="Remaining" value={fmt(order.remaining_quantity)} tone="warning" />
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1"><span className="text-muted-foreground">Progress</span><span className="font-num font-bold">{order.completion_pct}%</span></div>
                  <ProgressBar value={order.completion_pct} testid="prod-progress-bar" />
                </div>
              </>
            ) : <EmptyState title="Select an order" hint="Choose an order to see live totals." />}
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2"><History className="w-4 h-4" /> Recent Entries</h3>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {entries.length ? entries.slice(0, 12).map((e) => (
                <div key={e.id} className="flex items-center justify-between text-sm p-2 rounded-lg bg-secondary/40" data-testid="prod-entry-row">
                  <div>
                    <span className="font-medium">{e.department}</span>
                    <span className="text-muted-foreground"> · {e.shift?.split(" ")[1] || ""}</span>
                    <div className="text-xs text-muted-foreground">{e.entered_by} · {e.date}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="text-right font-num">
                      <span className="text-success font-bold">+{e.good_quantity}</span>
                      {e.rejected_quantity > 0 && <span className="text-destructive text-xs ml-2">-{e.rejected_quantity}</span>}
                    </div>
                    {canWrite("production") && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => delEntry(e.id, "Delete this entry? Order totals will be adjusted.")} data-testid={`delete-prod-${e.id}`}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>}
                  </div>
                </div>
              )) : <p className="text-sm text-muted-foreground">No entries yet.</p>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const c = tone === "success" ? "text-success" : tone === "danger" ? "text-destructive" : tone === "warning" ? "text-warning" : "text-foreground";
  return (
    <div className="rounded-lg bg-secondary/40 p-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`font-num font-bold text-xl ${c}`}>{value}</div>
    </div>
  );
}
